import { Component, OnInit } from '@angular/core';
import { HeaderService } from '@app/service/header/header.service';
import { StateService } from '@app/service/state/state.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RouteComponent } from '@app/route/route.component';
import { SeRoute } from '@assets/val/route-constants';
import { AisAccountService, AisBank, AisProvider, BankName } from '@app/service/ais-account/ais-account.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { PaymentMethod } from '@app/model/payment-request/payment-request.dto';

@Component({
  selector: 'se-ais-account',
  templateUrl: './se-ais-account.component.html',
  styleUrls: ['../../../page-shared.css', './se-ais-account.component.scss'],
})
export class SeAisAccountComponent extends RouteComponent implements OnInit {

  constructor(
    private aisAccountService: AisAccountService,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected router: Router,
    protected route: ActivatedRoute,
    private deviceDetectorService: DeviceDetectorService,
    private loggingService: LoggingService,
  ) {
    super(router, route, stateService, headerService);
  }

  loading: boolean = true;
  isPaymentFlow: boolean = false;
  banks: AisBank[] = [];
  displayedBanks: AisBank[] = [];

  searchQuery: string = '';
  isMobile: boolean = false;

  backNavigationAllowed: boolean = false;

  async ngOnInit(): Promise<void> {
    this.isMobile = this.deviceDetectorService.isMobile();
    this.loading = true;

    this.isPaymentFlow = this.state.paymentInformation?.paymentMethod === PaymentMethod.ZAVER_A2A;

    try {
      this.banks = await this.aisAccountService.getBanks(this.isPaymentFlow);
      this.updateDisplayedBanks();
      this.backNavigationAllowed = await this.aisAccountService.getAisBankBackNavigationAllowed();
    } catch (error) {
      console.error('Failed to load banks', error);
    } finally {
      this.loading = false;
    }
  }

  updateDisplayedBanks(): void {
    if (this.searchQuery) {
      this.displayedBanks = this.banks.filter(b =>
        b.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    } else {
      this.displayedBanks = this.banks;
    }
  }

  onSearch(event: any): void {
    this.searchQuery = event.target.value;
    this.updateDisplayedBanks();
  }

  async selectBank(aisBank: AisBank): Promise<void> {
    this.loading = true;

    let flowUrl: string
    try {
      if (this.isPaymentFlow) {
        flowUrl = await this.getPisUrl(aisBank);
      } else {
        flowUrl = await this.getAisUrl(aisBank);
      }
    } catch (e) {
      // Leave the flow url undefined to navigate (error page).
      this.loggingService.error("Error fetching the flow url! isPaymentFlow: " + this.isPaymentFlow, e);
    }

    if (flowUrl) {
      window.location.href = flowUrl;
    } else {
      this.loggingService.error("Error fetching URL - Navigation aborted!");
      this.navigate();
    }
  }

  async getAisUrl(aisBank: AisBank): Promise<string> {
    const redirectUrl = SeRoute.SELECT_AIS_CALLBACK;

    let flowUrl: string;
    if (aisBank.activeProvider === AisProvider.TINK) {
      flowUrl = (await this.aisAccountService.getTinkAisUrl(redirectUrl, aisBank.tinkSelectionKey)).url;
    } else if (aisBank.activeProvider === AisProvider.FINSHARK) {
      flowUrl = (await this.aisAccountService.getFinsharkAisUrl(redirectUrl, aisBank.finsharkSelectionKey)).url;
    } else {
      this.loggingService.error("No AIS provider detected, this is bad!");
    }

    return flowUrl;
  }

  async getPisUrl(aisBank: AisBank): Promise<string> {
    const redirectUrl = SeRoute.SELECT_AIS_CALLBACK;

    let flowUrl: string;
    if (aisBank.activePisProvider === AisProvider.TINK) {
      flowUrl = (await this.aisAccountService.getTinkPisUrl(redirectUrl, aisBank.tinkSelectionKey)).url;
    } else if (aisBank.activePisProvider === AisProvider.FINSHARK) {
      flowUrl = (await this.aisAccountService.getFinsharkPisUrl(redirectUrl, aisBank.finsharkSelectionKey)).url;
    } else {
      this.loggingService.error("No PIS provider detected, this is bad!");
    }

    return flowUrl;
  }

  async handleNavigateBack(): Promise<void> {
    const setResult = await this.aisAccountService.setAccountRequirement();
    if (setResult) {
      this.navigate();
    } else {
      this.loggingService.error("Couldn't set the account requirement. Backnavigation impossible, the AIS flow must be completed.");
      this.backNavigationAllowed = false;
    }
  }

  getBankDisplayName(bankKey: string): string {
    return BankName[bankKey];
  }
}
